import { useState } from "react";
import { C } from "../constants/theme";
import { Card } from "./ui";
import { useDashboardData } from "../hooks/useDashboardData";
import { calcCustoPedido } from "../utils/calcCusto";
import { normalizarStatus } from "../constants/statusPedido";
import { corFilamentoHex } from "../utils/corFilamento";
import "./dashboard.css";

const moeda = v => Number(v || 0).toLocaleString("pt-BR", { style:"currency", currency:"BRL" });
const MESES_CURTOS = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];
// Mesmo limiar de "acabando" das notificações push (useNotificacoesEventos.js)
// — abaixo disso soa o alerta lá, então faz sentido ser o mesmo corte aqui.
const LIMIAR_FILAMENTO_BAIXO = 0.2;
const LIMIAR_FILAMENTO_CRITICO = 0.08;

// ---------------------------------------------------------------------
// Período selecionado no cabeçalho — todo cálculo financeiro do painel
// deriva de um intervalo [inicio,fim] mais o intervalo equivalente
// imediatamente anterior (mesma duração), usado só pra comparação (↑/↓).
// ---------------------------------------------------------------------
const PERIODOS = [
  { id:"hoje",  label:"Hoje" },
  { id:"7d",    label:"7 dias" },
  { id:"30d",   label:"30 dias" },
  { id:"3m",    label:"3 meses" },
  { id:"ano",   label:"Este ano" },
  { id:"custom",label:"Personalizado" },
];

function rangeDoPeriodo(periodo, customIni, customFim) {
  const fim = new Date(); fim.setHours(23,59,59,999);
  const inicio = new Date();
  if (periodo === "hoje") inicio.setHours(0,0,0,0);
  else if (periodo === "7d") { inicio.setDate(inicio.getDate()-6); inicio.setHours(0,0,0,0); }
  else if (periodo === "30d") { inicio.setDate(inicio.getDate()-29); inicio.setHours(0,0,0,0); }
  else if (periodo === "3m") { inicio.setMonth(inicio.getMonth()-3); inicio.setHours(0,0,0,0); }
  else if (periodo === "ano") { return { inicio:new Date(fim.getFullYear(),0,1), fim }; }
  else if (periodo === "custom") {
    return {
      inicio: customIni ? new Date(customIni+"T00:00:00") : new Date(fim.getFullYear(),0,1),
      fim: customFim ? new Date(customFim+"T23:59:59") : fim,
    };
  }
  return { inicio, fim };
}
function rangeAnterior({ inicio, fim }) {
  const duracaoMs = fim.getTime() - inicio.getTime();
  const fimAnt = new Date(inicio.getTime() - 1);
  return { inicio:new Date(fimAnt.getTime() - duracaoMs), fim:fimAnt };
}
function dataDoPedido(p) {
  try {
    if (p.created_at?.toDate) return p.created_at.toDate();
    if (p.data) return new Date(p.data.split("/").reverse().join("-") + "T12:00:00");
  } catch { /* pedido sem data usável */ }
  return null;
}
const noRange = (d, r) => d && d >= r.inicio && d <= r.fim;
function dataDaVenda(v) { try { const d = new Date(v.data); return isNaN(d) ? null : d; } catch { return null; } }
function custoDaVenda(v) { return (v.itens || []).reduce((s, it) => s + (parseFloat(it.custo_unit) || 0) * (it.qty || 1), 0); }

// Faturamento/custo somam pedidos concluídos (encomendas) + vendas do PDV
// (Caixa — venda avulsa de peças em estoque, ex.: chaveiro), mesma soma que
// o Financeiro já faz — sem isso, quem vende mais pelo PDV que por encomenda
// via calculadora ficava com o painel inteiro subestimado.
function metricas(concluidos, vendas, range, impressoras, produtos) {
  const doRange = concluidos.filter(p => noRange(dataDoPedido(p), range));
  const vendasRange = vendas.filter(v => !v.estornada && noRange(dataDaVenda(v), range));
  const fatPedidos = doRange.reduce((s, p) => s + (parseFloat(p.valor) || 0), 0);
  const custoPedidos = doRange.reduce((s, p) => s + calcCustoPedido(p, impressoras, produtos).total, 0);
  const fatVendas = vendasRange.reduce((s, v) => s + (parseFloat(v.total) || 0), 0);
  const custoVendas = vendasRange.reduce((s, v) => s + custoDaVenda(v), 0);
  const faturamento = fatPedidos + fatVendas;
  const custo = custoPedidos + custoVendas;
  const lucro = faturamento - custo;
  const pedidosCount = doRange.length;
  const transacoes = pedidosCount + vendasRange.length;
  return {
    doRange, vendasRange, faturamento, custo, lucro, pedidosCount,
    ticketMedio: transacoes ? faturamento / transacoes : 0,
    margem: faturamento > 0 ? (lucro / faturamento) * 100 : 0,
  };
}
function variacao(atual, anterior) {
  if (!anterior) return atual ? null : 0;
  return ((atual - anterior) / Math.abs(anterior)) * 100;
}

function chaveEData(d, granularidade) {
  if (granularidade === "mes") { const k = d.getFullYear()+"-"+String(d.getMonth()+1).padStart(2,"0"); return { chave:k, label:MESES_CURTOS[d.getMonth()] }; }
  if (granularidade === "semana") { const seg=new Date(d); seg.setDate(seg.getDate()-((seg.getDay()+6)%7)); const k=seg.toISOString().slice(0,10); return { chave:k, label:String(seg.getDate()).padStart(2,"0")+"/"+String(seg.getMonth()+1).padStart(2,"0") }; }
  const k = d.toISOString().slice(0,10);
  return { chave:k, label:String(d.getDate()).padStart(2,"0")+"/"+String(d.getMonth()+1).padStart(2,"0") };
}
// Agrupa pedidos + vendas do período em pontos pro gráfico de evolução.
// Períodos curtos (7/30 dias) agrupam por dia; 3 meses por semana; o ano
// inteiro (ou um "Personalizado" longo) por mês — evita dezenas de pontos.
function serieTemporal(doRange, vendasRange, granularidade, impressoras, produtos) {
  const map = new Map();
  const add = (d, fat, custo) => {
    const { chave, label } = chaveEData(d, granularidade);
    if (!map.has(chave)) map.set(chave, { label, fat:0, custo:0 });
    const e = map.get(chave);
    e.fat += fat; e.custo += custo;
  };
  for (const p of doRange) { const d = dataDoPedido(p); if (d) add(d, parseFloat(p.valor) || 0, calcCustoPedido(p, impressoras, produtos).total); }
  for (const v of vendasRange) { const d = dataDaVenda(v); if (d) add(d, parseFloat(v.total) || 0, custoDaVenda(v)); }
  return [...map.entries()].sort(([a],[b]) => a < b ? -1 : 1)
    .map(([chave, v]) => ({ chave, label:v.label, faturamento:v.fat, lucro:v.fat - v.custo }));
}
function granularidadeDoPeriodo(periodo, range) {
  if (periodo === "3m") return "semana";
  if (periodo === "ano") return "mes";
  if (periodo === "custom") {
    const dias = (range.fim - range.inicio) / 86400000;
    return dias > 120 ? "mes" : dias > 31 ? "semana" : "dia";
  }
  return "dia"; // hoje / 7d / 30d
}

function valorOrcamento(o) { return (o.itens || []).reduce((s, it) => s + (parseFloat(it.preco) || 0) * (it.quantidade || 1), 0); }

// ---------------------------------------------------------------------
// Peças visuais
// ---------------------------------------------------------------------
function Variacao({ pct, positivoEhBom = true }) {
  if (pct === null) return <small className="dash-kpi-var dash-var-neutro">sem comparação</small>;
  const bom = pct === 0 ? null : positivoEhBom ? pct > 0 : pct < 0;
  const seta = pct > 0 ? "↑" : pct < 0 ? "↓" : "→";
  const cor = bom === null ? C.muted : bom ? C.success : C.danger;
  return <small className="dash-kpi-var" style={{ color:cor }}>{seta} {Math.abs(pct).toFixed(1)}% vs. período anterior</small>;
}

function Indicadores({ m, mAnterior }) {
  const itens = [
    { l:"Faturamento", v:moeda(m.faturamento), pct:variacao(m.faturamento, mAnterior.faturamento), bom:true },
    { l:"Lucro", v:moeda(m.lucro), pct:variacao(m.lucro, mAnterior.lucro), bom:true, cor:m.lucro >= 0 ? C.accent2 : C.danger },
    { l:"Custos", v:moeda(m.custo), pct:variacao(m.custo, mAnterior.custo), bom:false },
    { l:"Margem", v:`${m.margem.toFixed(1)}%`, sub:"margem de lucro" },
    { l:"Pedidos", v:m.pedidosCount, sub:"entregues no período" },
    { l:"Ticket Médio", v:moeda(m.ticketMedio), sub:"valor médio por pedido" },
  ];
  return (
    <section className="dash-kpis">
      {itens.map(it => (
        <div key={it.l} className="dash-kpi">
          <div className="dash-kpi-label">{it.l}</div>
          <div className="dash-kpi-value" style={{ color:it.cor || C.text }}>{it.v}</div>
          {it.pct !== undefined ? <Variacao pct={it.pct} positivoEhBom={it.bom} /> : <small className="dash-kpi-var dash-var-neutro">{it.sub}</small>}
        </div>
      ))}
    </section>
  );
}

function AnelMargem({ m }) {
  const prejuizo = m.lucro < 0;
  const temDados = m.faturamento > 0 || m.custo > 0;
  const setores = [
    { valor:m.custo, cor:"var(--pc-ui-38bdf8, #38bdf8)" },
    { valor:Math.max(0, m.lucro), cor:prejuizo ? "#ff8585" : "var(--pc-ui-00d4aa, #00d4aa)" },
  ];
  const total = setores.reduce((s, v) => s + v.valor, 0);
  let offset = 0;
  return (
    <div className="dash-donut">
      <svg viewBox="0 0 120 120" aria-hidden="true">
        <circle cx="60" cy="60" r="48" fill="none" stroke={C.panel} strokeWidth="12" />
        {total > 0 && setores.map((s, i) => {
          const pct = s.valor / total * 100; const start = offset; offset += pct;
          return pct > 0 && <circle key={i} cx="60" cy="60" r="48" fill="none" stroke={s.cor} strokeWidth="12" pathLength="100" strokeDasharray={`${pct} ${100-pct}`} strokeDashoffset={-start} transform="rotate(-90 60 60)" />;
        })}
      </svg>
      <div className="dash-donut-center">
        <strong style={{ color: temDados ? (prejuizo ? "#ff8585" : C.text) : C.muted }}>{temDados ? `${m.margem.toFixed(0)}%` : "—"}</strong>
        <span>{temDados ? (prejuizo ? "Prejuízo" : "Margem") : "Sem dados"}</span>
      </div>
    </div>
  );
}

function GraficoEvolucao({ serie }) {
  const [hover, setHover] = useState(null);
  if (serie.length < 2) return <p className="dash-empty">Selecione um período maior pra ver a evolução.</p>;
  const W = 560, H = 160, padL = 8, padR = 8, padT = 14, padB = 24;
  const maxV = Math.max(...serie.flatMap(d => [d.faturamento, d.lucro]), 1);
  const minV = Math.min(0, ...serie.flatMap(d => [d.faturamento, d.lucro]));
  const x = i => padL + (i / (serie.length - 1)) * (W - padL - padR);
  const y = v => padT + (1 - (v - minV) / (maxV - minV || 1)) * (H - padT - padB);
  const linha = campo => serie.map((d, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(d[campo]).toFixed(1)}`).join(" ");
  const passo = Math.max(1, Math.ceil(serie.length / 8));
  return (
    <div className="dash-chart-wrap">
      <svg viewBox={`0 0 ${W} ${H}`} className="dash-chart-svg">
        <line x1={padL} y1={y(0)} x2={W-padR} y2={y(0)} stroke={C.border} strokeWidth="1" />
        <path d={linha("faturamento")} fill="none" stroke="var(--pc-ui-38bdf8, #38bdf8)" strokeWidth="2" />
        <path d={linha("lucro")} fill="none" stroke="var(--pc-ui-00d4aa, #00d4aa)" strokeWidth="2" />
        {serie.map((d, i) => (
          <g key={d.chave} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
            <circle cx={x(i)} cy={y(d.faturamento)} r={hover===i?4:2.5} fill="var(--pc-ui-38bdf8, #38bdf8)" />
            <circle cx={x(i)} cy={y(d.lucro)} r={hover===i?4:2.5} fill="var(--pc-ui-00d4aa, #00d4aa)" />
            <rect x={x(i)-((W-padL-padR)/serie.length)/2} y={0} width={(W-padL-padR)/serie.length || 1} height={H} fill="transparent" />
            {i % passo === 0 && <text x={x(i)} y={H-6} textAnchor="middle" fill={C.muted} fontSize="9">{d.label}</text>}
          </g>
        ))}
      </svg>
      {hover !== null && (
        <div className="dash-tooltip" style={{ left:`${(x(hover)/W)*100}%` }}>
          <strong>{serie[hover].label}</strong>
          <div><i style={{ background:"var(--pc-ui-38bdf8, #38bdf8)" }} />Faturamento {moeda(serie[hover].faturamento)}</div>
          <div><i style={{ background:"var(--pc-ui-00d4aa, #00d4aa)" }} />Lucro {moeda(serie[hover].lucro)}</div>
        </div>
      )}
      <div className="dash-chart-legend">
        <span><i style={{ background:"var(--pc-ui-38bdf8, #38bdf8)" }} />Faturamento</span>
        <span><i style={{ background:"var(--pc-ui-00d4aa, #00d4aa)" }} />Lucro</span>
      </div>
    </div>
  );
}

function SecaoOrcamentos({ orcamentos, onGoTab }) {
  const abertos = orcamentos.filter(o => !o.aprovado_em);
  const aprovados = orcamentos.filter(o => o.aprovado_em);
  const conversao = orcamentos.length ? (aprovados.length / orcamentos.length) * 100 : 0;
  return (
    <Card className="dash-section">
      <div className="dash-section-head">
        <h3>Orçamentos</h3>
        <button className="dash-link" onClick={() => onGoTab("calc")}>Ver orçamentos →</button>
      </div>
      <div className="dash-orc-row"><span>Em aberto</span><b>{abertos.length}</b><span className="dash-orc-valor">{moeda(abertos.reduce((s,o)=>s+valorOrcamento(o),0))}</span></div>
      <div className="dash-orc-row"><span>Aprovados</span><b>{aprovados.length}</b><span className="dash-orc-valor">{moeda(aprovados.reduce((s,o)=>s+valorOrcamento(o),0))}</span></div>
      <div className="dash-orc-row"><span>Conversão</span><b>—</b><span className="dash-orc-valor">{conversao.toFixed(0)}%</span></div>
    </Card>
  );
}

// "Estoque baixo" (produtos com poucas/zero unidades) saiu — pra quem
// trabalha sob encomenda a maior parte do catálogo fica sempre em 0
// (nunca teve estoque pré-feito), então virava um alerta constante sem
// nenhum valor de decisão. Fica só o total de peças prontas em mãos.
function SecaoEstoque({ produtos, onGoTab }) {
  const itensEstoque = produtos.reduce((s, p) => s + (parseInt(p.estoque) || 0), 0);
  return (
    <Card className="dash-section">
      <div className="dash-section-head">
        <h3>Estoque</h3>
        <button className="dash-link" onClick={() => onGoTab("produtos")}>Ver estoque →</button>
      </div>
      <div className="dash-kpi" style={{ padding:0 }}>
        <div className="dash-kpi-label">Itens em estoque</div>
        <div className="dash-kpi-value" style={{ fontSize:28 }}>{itensEstoque}</div>
      </div>
    </Card>
  );
}

function SecaoProdutos({ ranking, onGoTab }) {
  return (
    <Card className="dash-section dash-card-produtos">
      <div className="dash-section-head">
        <h3>Produtos</h3>
        <button className="dash-link" onClick={() => onGoTab("produtos")}>Ver produtos →</button>
      </div>
      <div className="dash-prod-grid">
        <div>
          <h4>Mais vendidos</h4>
          {ranking.maisVendidos.length === 0 && <p className="dash-empty">Sem vendas no período.</p>}
          <ol>{ranking.maisVendidos.map(p => <li key={p.nome}><span>{p.nome}</span><b>{p.qtd}x</b></li>)}</ol>
        </div>
        <div>
          <h4>Maior lucro</h4>
          {ranking.maiorLucro.length === 0 && <p className="dash-empty">Sem vendas no período.</p>}
          <ol>{ranking.maiorLucro.map(p => <li key={p.nome}><span>{p.nome}</span><b style={{ color:p.lucro>=0?C.accent2:C.danger }}>{moeda(p.lucro)}</b></li>)}</ol>
        </div>
      </div>
    </Card>
  );
}

// Não entra na lista de Alertas genérica (o usuário pediu especificamente
// pra não virar "problema" ali) — é card próprio, e some sozinho quando não
// há nada abaixo do limiar (sem estado vazio/aviso à toa).
function SecaoFilamentos({ filamentos, coresCustom, onGoTab }) {
  const baixos = filamentos
    .map(f => {
      const peso = parseFloat(f.peso);
      if (!(peso > 0) || f.peso_restante == null) return null;
      const pct = Math.max(0, parseFloat(f.peso_restante) / peso);
      if (pct >= LIMIAR_FILAMENTO_BAIXO) return null;
      return { ...f, pct, critico: pct < LIMIAR_FILAMENTO_CRITICO };
    })
    .filter(Boolean)
    .sort((a, b) => a.pct - b.pct);
  if (baixos.length === 0) return null;

  return (
    <Card className="dash-section">
      <div className="dash-section-head">
        <h3>🧵 Filamentos acabando</h3>
        <button className="dash-link" onClick={() => onGoTab("filamentos")}>Ver filamentos →</button>
      </div>
      <ul className="dash-fil-list">
        {baixos.slice(0, 5).map(f => (
          <li key={f.id}>
            <button className="dash-fil-item" onClick={() => onGoTab("filamentos")}>
              <span className="dash-fil-swatch" style={{ background: corFilamentoHex(f.cor, coresCustom) || "#666" }} />
              <span className="dash-fil-info">
                <span className="dash-fil-nome">{[f.material, f.cor].filter(Boolean).join(" ") || f.nome || "Filamento"}</span>
                <span className="dash-fil-bar"><span className="dash-fil-bar-fill" style={{ width:`${Math.max(4, f.pct*100)}%`, background: f.critico ? C.danger : C.warning }} /></span>
              </span>
              <span className="dash-fil-pct" style={{ color: f.critico ? C.danger : C.warning }}>{Math.round(f.pct*100)}%</span>
            </button>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function Alertas({ itens }) {
  return (
    <div className="dash-alertas">
      {itens.map((a, i) => (
        <div key={i} className={`dash-alerta dash-alerta-${a.tipo}`}>{a.tipo === "ok" ? "✓" : "⚠"} {a.texto}</div>
      ))}
    </div>
  );
}

// Dashboard: redesenhado em 2026-09-18 como painel de decisão (não depósito
// de informação) — cabeçalho com seletor de período, indicadores principais,
// margem + evolução financeira, orçamentos, estoque, produtos e alertas.
// Status ao vivo da Bambu Lab segue fora do app — ver
// BAMBU_DASHBOARD_HANDOFF.md, que trata de outro assunto.
export default function Dashboard({ onGoTab, coresCustom }) {
  const { pedidos, impressoras, produtos, orcamentos, vendas, filamentos, loading } = useDashboardData();

  const [periodo, setPeriodo] = useState("30d");
  const [customIni, setCustomIni] = useState("");
  const [customFim, setCustomFim] = useState("");
  const [refreshTick, setRefreshTick] = useState(0);

  if (loading) return <div className="dash-loading">⏳ Carregando painel...</div>;

  const concluidos = pedidos.filter(p => ["entregue","concluido"].includes(normalizarStatus(p.status)));
  const range = rangeDoPeriodo(periodo, customIni, customFim);
  const rangeAnt = rangeAnterior(range);
  const m = metricas(concluidos, vendas, range, impressoras, produtos);
  const mAnt = metricas(concluidos, vendas, rangeAnt, impressoras, produtos);
  const granularidade = granularidadeDoPeriodo(periodo, range);
  const serie = serieTemporal(m.doRange, m.vendasRange, granularidade, impressoras, produtos);

  // Ranking por produto soma encomendas (pedidos.produto, 1 peça cada) e
  // PDV (vendas.itens, com qty e preço próprios) — sem isso, quem vende
  // mais avulso no balcão (ex.: chaveiro) nunca aparecia aqui.
  const ranking = (() => {
    const porNome = new Map();
    const add = (nome, qtd, fat, custo) => {
      if (!porNome.has(nome)) porNome.set(nome, { qtd:0, fat:0, custo:0 });
      const e = porNome.get(nome);
      e.qtd += qtd; e.fat += fat; e.custo += custo;
    };
    for (const p of m.doRange) add(p.produto || "—", 1, parseFloat(p.valor) || 0, calcCustoPedido(p, impressoras, produtos).total);
    for (const v of m.vendasRange) for (const it of v.itens || []) add(it.nome || "—", it.qty || 1, (parseFloat(it.preco) || 0) * (it.qty || 1), (parseFloat(it.custo_unit) || 0) * (it.qty || 1));
    const lista = [...porNome.entries()].map(([nome, v]) => ({ nome, qtd:v.qtd, lucro:v.fat - v.custo }));
    return {
      maisVendidos: [...lista].sort((a,b)=>b.qtd-a.qtd).slice(0,5),
      maiorLucro: [...lista].sort((a,b)=>b.lucro-a.lucro).slice(0,5),
    };
  })();

  const orcamentosAbertosCount = orcamentos.filter(o => !o.aprovado_em).length;
  const alertasItens = [];
  if (orcamentosAbertosCount > 0) alertasItens.push({ tipo:"warn", texto:`${orcamentosAbertosCount} orçamento${orcamentosAbertosCount===1?"":"s"} aguardando aprovação` });
  if (m.lucro < 0) alertasItens.push({ tipo:"warn", texto:"Prejuízo no período selecionado" });

  return (
    <div className="dash-root">
      <header className="dash-header">
        <div>
          <h1>Visão geral</h1>
          <p>Resumo do desempenho do seu negócio</p>
        </div>
        <div className="dash-header-actions">
          <select className="dash-select" value={periodo} onChange={e => setPeriodo(e.target.value)}>
            {PERIODOS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
          {periodo === "custom" && (
            <span className="dash-custom-range">
              <input type="date" value={customIni} onChange={e => setCustomIni(e.target.value)} />
              <input type="date" value={customFim} onChange={e => setCustomFim(e.target.value)} />
            </span>
          )}
          <button className="dash-icon-btn" title="Atualizar" onClick={() => setRefreshTick(t => t + 1)}>🔄</button>
          <button className="dash-cta" onClick={() => onGoTab("calc")}>+ Adicionar orçamento</button>
        </div>
      </header>

      <Indicadores key={refreshTick} m={m} mAnterior={mAnt} />

      <section className="dash-row-2col">
        <Card className="dash-section dash-section-margem">
          <h3>Margem de lucro</h3>
          <AnelMargem m={m} />
          <div className="dash-margem-legenda">
            <div><span>Custo</span><b>{moeda(m.custo)}</b></div>
            <div><span>Lucro</span><b style={{ color:m.lucro>=0?C.accent2:C.danger }}>{moeda(m.lucro)}</b></div>
          </div>
        </Card>
        <Card className="dash-section dash-section-evolucao">
          <h3>Desempenho financeiro</h3>
          <GraficoEvolucao serie={serie} />
        </Card>
      </section>

      {alertasItens.length > 0 && <Alertas itens={alertasItens} />}

      <section className="dash-row-cards">
        <SecaoOrcamentos orcamentos={orcamentos} onGoTab={onGoTab} />
        <div className="dash-card-estoque-stack">
          <SecaoEstoque produtos={produtos} onGoTab={onGoTab} />
          <SecaoFilamentos filamentos={filamentos} coresCustom={coresCustom} onGoTab={onGoTab} />
        </div>
        <SecaoProdutos ranking={ranking} onGoTab={onGoTab} />
      </section>

    </div>
  );
}
